import React, { useState } from "react";
import ImpressumModal from "./ImpressumModal";
import LegalNoticesModal from "./LegalNoticesModal";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t } from "../utils/translations";
import { APP_VERSION_LABEL, COPYRIGHT_LABEL } from "../appVersion";

const TelegramIcon = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M9.993 15.674 9.8 19.2c.276 0 .396-.118.54-.26l1.296-1.24 2.688 1.968c.492.272.84.128.972-.456l1.764-8.28c.168-.776-.288-1.08-.756-.9L5.1 13.52c-.744.288-.732.696-.132.88l3.12.976 7.248-4.572c.342-.216.654-.096.396.12l-5.739 4.75Z" />
  </svg>
);

const XIcon = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2H21l-6.56 7.497L22.5 22h-6.328l-4.955-6.474L5.55 22H2.79l7.017-8.018L1.5 2h6.488l4.478 5.91L18.244 2Zm-1.11 18.104h1.527L7.158 3.796H5.52l11.614 16.308Z" />
  </svg>
);

const sourceBadgeBase =
  "inline-flex h-8 items-center gap-2 rounded-full border border-slate-200 bg-white/85 px-3.5 text-xs font-medium leading-none text-slate-600 shadow-sm transition-all duration-200";

const Footer = ({ onOpenSupport }) => {
  const [showImpressum, setShowImpressum] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const { language } = useLanguage();
  return (
    <>
      <footer className="w-full border-t border-slate-200/80 bg-white/90 backdrop-blur-sm">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 items-center gap-3 px-4 pt-5 pb-4 sm:px-6 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] xl:gap-5">
          <div className="flex min-w-0 items-center justify-center xl:justify-start">
            <div
              className={`${sourceBadgeBase} hover:border-slate-300 hover:bg-slate-50`}
              aria-label="Powered by CoinGecko API and vechainstats.com APIs"
            >
              <span className="font-semibold text-slate-700">Powered by</span>

              <a
                href="https://www.coingecko.com/en/api"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full transition-colors hover:text-emerald-700"
                title="Powered by CoinGecko API"
                aria-label="Powered by CoinGecko API"
              >
                <img
                  src="/brand/coingecko-api-lockup.png"
                  alt="CoinGecko API"
                  className="h-[18px] w-auto object-contain"
                />
              </a>

              <span className="text-slate-300">•</span>

              <a
                href="https://vechainstats.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full transition-colors hover:text-blue-700"
                title="vechainstats.com APIs"
                aria-label="vechainstats.com APIs"
              >
                <img
                  src="/vs.jpg"
                  alt="VeChainStats"
                  className="h-[18px] w-[18px] rounded-sm object-cover"
                />
                <span>vechainstats.com APIs</span>
              </a>
            </div>
          </div>

          <div className="flex min-w-0 flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[13px] text-slate-500">
            <span className="font-medium text-slate-700">{COPYRIGHT_LABEL}</span>
            <span className="text-slate-300">•</span>
            <span>{t("allRightsReserved", language)}.</span>
            <span className="text-slate-300">•</span>
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-slate-600">
              {APP_VERSION_LABEL}
            </span>
            <span className="text-slate-300">•</span>
            <button
              type="button"
              onClick={() => setShowImpressum(true)}
              className="font-medium text-slate-600 transition-colors duration-200 hover:text-blue-600"
            >
              {t("imprint", language)}
            </button>
            <span className="text-slate-300">•</span>
            <button
              type="button"
              onClick={() => setShowLegal(true)}
              className="font-medium text-slate-600 transition-colors duration-200 hover:text-blue-600"
            >
              {t("legalNotices", language)}
            </button>
            <span className="text-slate-300">•</span>
            <button
              type="button"
              onClick={() => onOpenSupport?.()}
              className="font-medium text-slate-600 transition-colors duration-200 hover:text-blue-600"
            >
              {t("support", language)}
            </button>
          </div>

          <div className="flex min-w-0 items-center justify-center gap-2 xl:justify-end">
            <a
              href="https://t.me/V3TaxHub"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="V3TaxHub auf Telegram öffnen"
              title="Telegram"
              className="inline-flex h-8 items-center gap-2 leading-none rounded-full border border-sky-100 bg-sky-50/80 px-3 text-sm font-semibold text-[#229ED9] shadow-sm hover:border-sky-200 hover:bg-sky-100 hover:text-[#1B8FCB] transition-all duration-200"
            >
              <TelegramIcon className="w-5 h-5" />
              <span className="hidden sm:inline">Telegram</span>
            </a>

            <a
              href="https://x.com/V3Taxapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="V3Tax auf X öffnen"
              title="X"
              className="inline-flex h-8 items-center justify-center leading-none rounded-full border border-slate-200 bg-slate-900 px-3 text-white shadow-sm hover:bg-black hover:border-slate-900 transition-all duration-200"
            >
              <XIcon className="w-5 h-5" />
            </a>
          </div>
        </div>
      </footer>

      <ImpressumModal
        isOpen={showImpressum}
        onClose={() => setShowImpressum(false)}
      />
      <LegalNoticesModal
        open={showLegal}
        onClose={() => setShowLegal(false)}
        language={language}
      />
    </>
  );
};

export default Footer;
